import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listAdminAppointments } from "../../Redux/actions/adminActions/adminAppointmentActions.js";
import API from "../../api.js";
import Loading from "../../components/Loading.jsx";
import Error from "../../components/Error.jsx";
import ViewAppointmentModal from "../../components/adminComponents/Appointments/ViewAppointmentModal.jsx";
import ReportGenerator from "../../components/adminComponents/Appointments/ReportGenerator.jsx";
import {
  Calendar,
  Search,
  Filter,
  Eye,
  FileText,
  Clock,
  User,
  DollarSign,
  Building2,
  Stethoscope,
  CalendarCheck,
  X,
} from "lucide-react";

// Date yyyy-mm-dd
const t = new Date();
const yyyy = t.getFullYear();
const mm = String(t.getMonth() + 1).padStart(2, "0");
const dd = String(t.getDate()).padStart(2, "0");
const iso = `${yyyy}-${mm}-${dd}`;

// Filters
const initialFilters = {
  status: "",
  doctorId: "",
  department: "",
  date: "",
  createdAt: "",
  search: "",
  mrCode: "",
};

const ManageAppointments = () => {
  const dispatch = useDispatch();
  const {
    appointments = [],
    loading,
    error,
  } = useSelector((state) => state.adminAppointments || {});

  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState("");

  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [viewModalShow, setViewModalShow] = useState(false);

  // Fetch Doctors + Departments once
  useEffect(() => {
    API.get("/admin/doctors")
      .then((res) => setDoctors(res.data || []))
      .catch((err) => console.error(err));

    API.get("/admin/departments")
      .then((res) => setDepartments(res.data || []))
      .catch((err) => console.error(err));
  }, []);

  // Debounce Search Effect (500ms)
  useEffect(() => {
    const delay = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
    }, 500);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  // Reload whenever filters change
  useEffect(() => {
    dispatch(listAdminAppointments(filters));
  }, [dispatch, filters]);

  const handleView = (appt) => {
    setSelectedAppointment(appt);
    setViewModalShow(true);
  };

  const applyTodayFilter = () => {
    setFilters((prev) => ({ ...prev, date: iso }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setSearchTerm("");
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: "#ffc107", text: "#000" },
      confirmed: { bg: "#28a745", text: "#fff" },
      rejected: { bg: "#dc3545", text: "#fff" },
      completed: { bg: "#6c757d", text: "#fff" },
    };
    return badges[status] || badges.pending;
  };

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem" }}>
      <div className="container-fluid">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "2rem",
                borderRadius: "16px",
                color: "white",
                boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h2 className="mb-2 fw-bold d-flex align-items-center">
                    <Calendar size={32} className="me-3" />
                    Appointment Management
                  </h2>
                  <p className="mb-0" style={{ opacity: 0.9 }}>
                    View and manage all hospital appointments
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                      Total Appointments
                    </p>
                    <h2 className="mb-0 fw-bold">{appointments?.length || 0}</h2>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      padding: "1rem",
                    }}
                  >
                    <Calendar size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                      Pending
                    </p>
                    <h2 className="mb-0 fw-bold">
                      {appointments?.filter((a) => a.status === "pending").length || 0}
                    </h2>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      padding: "1rem",
                    }}
                  >
                    <Clock size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                      Confirmed
                    </p>
                    <h2 className="mb-0 fw-bold">
                      {appointments?.filter((a) => a.status === "confirmed").length || 0}
                    </h2>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      padding: "1rem",
                    }}
                  >
                    <CalendarCheck size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                color: "white",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                      Completed
                    </p>
                    <h2 className="mb-0 fw-bold">
                      {appointments?.filter((a) => a.status === "completed").length || 0}
                    </h2>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      padding: "1rem",
                    }}
                  >
                    <FileText size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Generator */}
        <div className="row mb-4">
          <div className="col-12">
            <ReportGenerator filters={filters} />
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <Filter size={20} className="me-2" style={{ color: "#667eea" }} />
                  <h5 className="mb-0 fw-semibold">Filters</h5>
                </div>
                <div className="row g-3">
                  <div className="col-md-2">
                    <label className="form-label small text-muted fw-semibold">
                      Created Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.createdAt}
                      onChange={(e) =>
                        setFilters({ ...filters, createdAt: e.target.value })
                      }
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small text-muted fw-semibold">
                      Search MR Code
                    </label>
                    <div className="position-relative">
                      <Search
                        size={16}
                        style={{
                          position: "absolute",
                          left: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#6c757d",
                        }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="MR-00001"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ borderRadius: "8px", paddingLeft: "2.5rem" }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small text-muted fw-semibold">
                      Status
                    </label>
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) =>
                        setFilters({ ...filters, status: e.target.value })
                      }
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="rejected">Rejected</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small text-muted fw-semibold">
                      Department
                    </label>
                    <select
                      className="form-select"
                      value={filters.department}
                      onChange={(e) =>
                        setFilters({ ...filters, department: e.target.value })
                      }
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">All Departments</option>
                      {departments.map((d) => (
                        <option key={d._id} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small text-muted fw-semibold">
                      Doctor
                    </label>
                    <select
                      className="form-select"
                      value={filters.doctorId}
                      onChange={(e) =>
                        setFilters({ ...filters, doctorId: e.target.value })
                      }
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">All Doctors</option>
                      {doctors.map((d) => (
                        <option key={d._id} value={d._id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2 d-flex align-items-end gap-2">
                    <button
                      className="btn btn-primary flex-grow-1"
                      onClick={applyTodayFilter}
                      style={{ borderRadius: "8px", fontWeight: "500" }}
                    >
                      <CalendarCheck size={16} className="me-1" />
                      Today
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={clearFilters}
                      style={{ borderRadius: "8px", fontWeight: "500" }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="row">
          <div className="col-12">
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div className="card-body p-0">
                {loading && (
                  <div className="text-center py-5">
                    <Loading />
                  </div>
                )}
                {error && (
                  <div className="p-4">
                    <Error message={error} />
                  </div>
                )}

                {!loading && !error && (
                  <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <table className="table table-hover mb-0" style={{ minWidth: "1400px" }}>
                      <thead
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            MR-Code
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Patient
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Appt #
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Department
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Date
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Time
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Doctor
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Fee
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Payment
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none" }}>
                            Status
                          </th>
                          <th style={{ padding: "1.25rem 1.5rem", fontWeight: 600, borderBottom: "none", textAlign: "center" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments && appointments.length ? (
                          appointments.map((appt, idx) => (
                            <tr
                              key={`${appt._id}-${idx}`}
                              style={{ borderBottom: "1px solid #f0f0f0", transition: "background 0.2s" }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "#f8f9fa")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            >
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <span className="fw-semibold text-primary">
                                  {appt.mrCode || "-"}
                                </span>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <User size={16} color="#6c757d" />
                                  <span className="fw-semibold">{appt.pName}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <span className="badge bg-secondary">
                                  {appt.appointmentNumber || "-"}
                                </span>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Building2 size={16} color="#6c757d" />
                                  <span>{appt.department}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Calendar size={16} color="#6c757d" />
                                  <span>{appt.date || "-"}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Clock size={16} color="#6c757d" />
                                  <span>{appt.time || "-"}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Stethoscope size={16} color="#6c757d" />
                                  <span>{appt.doctor?.name || "-"}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <DollarSign size={16} color="#6c757d" />
                                  <span className="fw-semibold">
                                    Rs. {appt.payment?.feePaid ?? 0}
                                  </span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <span
                                  className={`badge ${
                                    appt.payment?.status === "paid"
                                      ? "bg-success"
                                      : "bg-warning"
                                  }`}
                                >
                                  {appt.payment?.status === "paid" ? "Paid" : "Unpaid"}
                                </span>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <span
                                  className="badge"
                                  style={{
                                    background: getStatusBadge(appt.status).bg,
                                    color: getStatusBadge(appt.status).text,
                                    padding: "0.4rem 0.8rem",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {appt.status}
                                </span>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem", textAlign: "center" }}>
                                <button
                                  className="btn btn-sm btn-outline-info d-flex align-items-center"
                                  onClick={() => handleView(appt)}
                                  style={{ borderRadius: "8px", padding: "0.4rem 1rem", margin: "0 auto" }}
                                >
                                  <Eye size={14} className="me-1" />
                                  View
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="11"
                              className="text-center py-5"
                              style={{ color: "#6c757d" }}
                            >
                              <Calendar size={64} color="#dee2e6" className="mb-3" />
                              <p className="mb-0 fw-semibold" style={{ fontSize: "1.1rem" }}>
                                No appointments found
                              </p>
                              <p className="text-muted small">
                                Try adjusting your search or filter criteria
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ViewAppointmentModal
          show={viewModalShow}
          appointment={selectedAppointment}
          onHide={() => {
            setViewModalShow(false);
            setSelectedAppointment(null);
            dispatch(listAdminAppointments(filters));
          }}
        />
      </div>
    </div>
  );
};

export default ManageAppointments;